import { useEffect } from 'react';
import type { BlockVariation } from '@root/@types/database';
import type { BlockVisibilityOutput } from '@root/@types/types';
import { isEmpty } from 'lodash-es';

import { useVariationsTabsStore } from './store';

interface VariationReadModelInput {
  id?: string;
  variations?: BlockVariation[];
  selectedVariationId?: string;
  visibility?: { variations?: unknown };
  visibilityComputed?: { variations?: BlockVisibilityOutput[] };
}

export const useVariationInView = (block: VariationReadModelInput | undefined) => {
  const { variations, selectedVariationId } = block ?? {};
  const blockId = block?.id ?? '';

  const { currentVariationIdx, setCurrentVariationIdx } = useVariationsTabsStore(
    ({ getBlockCurrentVariationIdx, setCurrentVariationIdx }) => ({
      currentVariationIdx: getBlockCurrentVariationIdx(blockId),
      setCurrentVariationIdx,
    }),
  );

  const setCurrentIdx = (newValue: number) => setCurrentVariationIdx(blockId, Math.max(newValue, 0));

  const setCurrent = (id?: string) => {
    const list = variations ?? [];
    if (!list.length || !id) {
      setCurrentVariationIdx(blockId, 0);
      return;
    }

    const variation = list.find((v) => v.id === id);
    const idx = variation ? list.indexOf(variation) : 0;
    setCurrentIdx(idx);
  };

  const variationsCount = variations?.length ?? 0;

  useEffect(() => {
    if (variationsCount === currentVariationIdx) {
      setCurrentIdx(variationsCount - 1);
    }
    // eslint-disable-next-line -- not interested in setCurrentIdx changes
  }, [currentVariationIdx, variationsCount]);

  return {
    currentVariationIdx,
    setCurrentIdx,
    setCurrent,
    variationInView: variations?.[currentVariationIdx]?.id,
    variationsCount,
    selectedVariationId,
    visibilityDetails: block?.visibilityComputed?.variations,
    hasLogic: !isEmpty(block?.visibility?.variations),
    variations: variations ?? [],
  };
};
